import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Single-photo-at-a-time carousel for department glimpses (and similar use cases).
 * When the photo array has 1 image, renders just that image (no controls).
 * When >1, renders prev/next arrows + dot indicators + counter badge.
 *
 * Each photo click invokes `onPhotoClick(index)` so the existing lightbox
 * integration in each department page keeps working.
 *
 * Props:
 *   photos        — [{ url, public_id? }]
 *   onPhotoClick  — fn(index) — called when the user clicks the image
 *   name          — string — used for alt text + aria
 *   accentClass   — Tailwind classes for the active-dot color (default amber)
 *   testidPrefix  — prefix for data-testid on slides and controls
 *   className     — extra wrapper class on the image box (defaults to a square)
 *   autoplay      — auto-advance every `autoplayMs` ms (pauses on hover); default false
 *   autoplayMs    — interval in ms (default 5000)
 */
export default function PhotoCarousel({
  photos = [],
  onPhotoClick,
  name = "",
  accentClass = "bg-amber-500",
  inactiveDotClass = "bg-slate-300",
  controlClass = "bg-white/85 hover:bg-white text-slate-800 shadow-md",
  testidPrefix = "dept-photo",
  className = "w-full aspect-square rounded-lg overflow-hidden ring-1 ring-slate-200",
  autoplay = false,
  autoplayMs = 5000,
}) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = photos?.length || 0;
  // Reset to first slide whenever the photo set changes (e.g., switching department)
  useEffect(() => {
    setI(0);
  }, [photos]);
  // Autoplay: advance only when enabled, >1 photo, and not hovered/focused.
  useEffect(() => {
    if (!autoplay || total <= 1 || paused) return undefined;
    const id = setInterval(() => setI((x) => (x + 1) % total), autoplayMs);
    return () => clearInterval(id);
  }, [autoplay, autoplayMs, total, paused]);

  if (!total) return null;
  const cur = photos[Math.min(i, total - 1)] || photos[0];
  const goPrev = () => setI((x) => (x - 1 + total) % total);
  const goNext = () => setI((x) => (x + 1) % total);

  return (
    <div
      className="relative"
      data-testid={`${testidPrefix}-carousel`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <button
        type="button"
        onClick={() => onPhotoClick && onPhotoClick(i)}
        data-testid={`${testidPrefix}-${i}`}
        className={`block w-full text-left ${className} group`}
        aria-label={`Photo ${i + 1} of ${total}${name ? ` — ${name}` : ""}`}
      >
        <img
          src={cur.url}
          alt={`${name} — ${i + 1}`}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
          loading="lazy"
        />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            data-testid={`${testidPrefix}-prev`}
            aria-label="Previous photo"
            className={`absolute top-1/2 -translate-y-1/2 left-2 w-9 h-9 rounded-full flex items-center justify-center ${controlClass}`}
          >
            <ChevronLeft size={18} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={goNext}
            data-testid={`${testidPrefix}-next`}
            aria-label="Next photo"
            className={`absolute top-1/2 -translate-y-1/2 right-2 w-9 h-9 rounded-full flex items-center justify-center ${controlClass}`}
          >
            <ChevronRight size={18} strokeWidth={2} />
          </button>
          <div className="absolute top-2 right-2 bg-black/65 text-white text-[11px] px-2 py-0.5 rounded-sm font-semibold backdrop-blur-sm">
            {i + 1} / {total}
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5" data-testid={`${testidPrefix}-dots`}>
            {photos.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setI(idx)}
                aria-label={`Go to photo ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? `w-6 ${accentClass}` : `w-1.5 ${inactiveDotClass}`}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
